import { spacing as designSpacing, spacing } from "../../../app/design-system";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { Form,Typography } from "../../../../../../../shared/frontend/ui";
import type { ModalFuncProps } from "../../../../../../../shared/frontend/ui";
import dayjs,{ type Dayjs } from "dayjs";
import React from "react";
import {
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CANCELLED,
  CONTRACT_STATUS_ENDED,
  CONTRACT_STATUS_PAUSED
} from "../../../app/constants/contractStatus";
import { PAYMENT_METHOD_SEPA } from "../../../app/utils/paymentMethodCodes";
import type {
  ApiContractPackageDto,
  ApiPersonServiceDto,
  ApiWalletDto,
  ContractRow,
  PersonServiceRow
} from "./personDetailTypes";
import { mapApiPackageToContractRow } from "./personDetailUtils";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { ACCESS_SCOPE_ANY_STUDIO } from "../../../app/constants/accessScope";
import type { TimeUnitValue } from "../../../app/constants/timeUnit";

/** Ergebnis von String(null) / String(undefined) bei leeren Formularwerten. */
const STRINGIFIED_NULL = "null";
const STRINGIFIED_UNDEFINED = "undefined";
/** Verzögerung (ms), bis die SEPA-Gruppe nach Wechsel der Zahlart bzw. dem Öffnen des Formulars geleert wird. */
const SEPA_GROUP_RESET_DELAY_MS = 200;
const SEPA_GROUP_PREFILL_RESET_DELAY_MS = 100;

type PersonContractEditApiClient = {
  get<T>(url: string, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
  put<T = unknown>(url: string, body?: unknown, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
  del<T = unknown>(url: string, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
};

export type PersonContractPackageEditFormValues = {
  planName?: string;
  studioId?: string | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  closingDate?: Dayjs | null;
  restPeriodTemplateId?: string | null;
  cancellationModelId?: string | null;
  paymentMethod?: number | null;
  sepaGroupId?: string | null;
  note?: string | null;
  contractBlockName?: string;
  contractBlockValue?: number | null;
};

type UsePersonContractEditControllerArgs = {
  api: PersonContractEditApiClient;
  personId: string;
  canWriteContracts: boolean;
  sepaGroups: Array<{ id: string; name: string; isActive: boolean }>;
  bookedContractIds: Set<string>;
  message: {
    success(content: React.ReactNode): void;
    error(content: React.ReactNode): void;
  };
  modal: {
    confirm(config: ModalFuncProps): unknown;
  };
  text: (key: string) => string;
};

type RelatedContractEditSnapshot = {
  id: string;
  sourceContractPackageId?: string | null;
  name?: string | null;
  isPrimary?: boolean | null;
  startDate?: string | null;
  endDate?: string | null;
  value?: number | null;
  usageLimitValue?: number | null;
  usageLimitUnit?: TimeUnitValue | null;
  paymentCycleValue?: number | null;
  paymentCycleUnit?: number | null;
  durationValue?: number | null;
  durationUnit?: number | null;
  renewalValue?: number | null;
  renewalUnit?: number | null;
  allowsAccess?: boolean | null;
  homeStudioId?: string | null;
  accessScope?: number | null;
  allowedStudioIds?: unknown[] | null;
  requiresNoArrears?: boolean | null;
  isActive?: boolean | null;
  paymentMethod?: number | null;
  sepaGroupId?: string | null;
};

function normalizeSepaGroupId(value: unknown, paymentMethod: number | null): string | null {
  if (paymentMethod !== PAYMENT_METHOD_SEPA) return null;
  if (value == null || value === "") return null;
  const textValue = String(value);
  if (textValue === STRINGIFIED_NULL || textValue === STRINGIFIED_UNDEFINED) return null;
  return textValue;
}

function normalizeExistingSepaGroupId(value: unknown): string | null {
  if (value == null || value === "") return null;
  const textValue = String(value);
  if (textValue === STRINGIFIED_NULL || textValue === STRINGIFIED_UNDEFINED) return null;
  return textValue;
}

function displayContractPackageSaveError(error: unknown, text: (key: string) => string): string {
  const candidate = error as { message?: unknown; response?: { data?: { message?: unknown } } };
  const errorMsg =
    (typeof candidate?.message === "string" && candidate.message) ||
    (typeof candidate?.response?.data?.message === "string" && candidate.response.data.message) ||
    String(error);

  if (
    errorMsg.includes("Name fehlt") ||
    errorMsg.includes("Name field is required") ||
    errorMsg.includes("Name: The Name field is required")
  ) {
    return text("Name: The Name field is required");
  }
  if (errorMsg.includes("PlanName") && errorMsg.includes("leer")) {
    return text("Contract package name required");
  }
  if (errorMsg.includes("not found") || errorMsg.includes("NotFound")) {
    return text("Contract package not found");
  }
  return errorMsg;
}

function setServiceEditFormValues(
  form: FormInstance,
  sr: PersonServiceRow,
  sepaGroups: Array<{ id: string; isActive: boolean }>
) {
  const paymentMethodValue = sr.paymentMethod != null ? Number(sr.paymentMethod) : null;
  let sepaGroupIdValue = sr.sepaGroupId ?? null;
  if (paymentMethodValue === PAYMENT_METHOD_SEPA && !sepaGroupIdValue && sepaGroups.length > 0) {
    const firstActiveSepaGroup = sepaGroups.find((g) => g.isActive);
    if (firstActiveSepaGroup) sepaGroupIdValue = firstActiveSepaGroup.id;
  }
  form.setFieldsValue({
    name: sr.name,
    sepaGroupId: sepaGroupIdValue,
    startDate: sr.startDate ? dayjs(String(sr.startDate).slice(0, ISO_DATE_LENGTH)) : null,
    endDate: sr.endDate ? dayjs(String(sr.endDate).slice(0, ISO_DATE_LENGTH)) : null,
    value: sr.value ?? null,
    paymentCycleValue: sr.paymentCycleValue ?? null,
    paymentCycleUnit: sr.paymentCycleUnit ?? null,
    paymentMethod: paymentMethodValue,
    isLocked: sr.isActive === false
  });
}

function buildContractPackageRequestBody(
  contractEdit: ContractRow,
  values: PersonContractPackageEditFormValues,
  contractEditIsLocked: boolean,
  sepaGroupIdToSend: string | null,
  planName: string,
  startDate: string,
  endDate: string | null
) {
  const currentStatus = Number(contractEdit.status || 0);
  const newStatus = contractEditIsLocked
    ? CONTRACT_STATUS_PAUSED
    : currentStatus === CONTRACT_STATUS_PAUSED
      ? CONTRACT_STATUS_ACTIVE
      : currentStatus;

  return {
    studioId: values.studioId ?? null,
    status: newStatus,
    startDate,
    endDate,
    billingCycle: contractEdit.billingCycle ?? 0,
    termDefinitionId: contractEdit.termDefinitionId ?? null,
    termName: contractEdit.termName ?? null,
    termUnit: contractEdit.termUnit ?? null,
    termValue: contractEdit.termValue ?? null,
    restPeriodTemplateId: contractEdit.restPeriodTemplateId ? String(contractEdit.restPeriodTemplateId) : null,
    cancellationModelId: contractEdit.cancellationModelId ? String(contractEdit.cancellationModelId) : null,
    accessScope: contractEdit.accessScope ?? ACCESS_SCOPE_ANY_STUDIO,
    allowedStudioIds: (contractEdit.allowedStudioIds || []).map(String),
    requiresNoArrears: contractEdit.requiresNoArrears !== false,
    note: typeof values.note === "string" && values.note.trim() ? values.note.trim() : null,
    paymentMethod: values.paymentMethod != null ? Number(values.paymentMethod) : null,
    sepaGroupId: sepaGroupIdToSend,
    planName
  };
}

function buildRelatedContractRequestBody(
  contract: RelatedContractEditSnapshot,
  values: PersonContractPackageEditFormValues,
  useMergedBlockFields: boolean,
  contractEditIsLocked: boolean,
  startDate: string,
  endDate: string | null,
  sepaGroupIdValue: unknown
) {
  const sepaGroupIdToUse = normalizeExistingSepaGroupId(sepaGroupIdValue) ?? contract.sepaGroupId ?? null;
  const isPrimary = contract.isPrimary === true;
  const contractStartDate = isPrimary && startDate ? startDate : String(contract.startDate || "").slice(0, ISO_DATE_LENGTH);
  const contractEndDate = isPrimary && endDate ? endDate : contract.endDate ? String(contract.endDate).slice(0, ISO_DATE_LENGTH) : null;
  const blockName = useMergedBlockFields ? String(values.contractBlockName ?? "").trim() : String(contract.name || "").trim();
  const blockValue = useMergedBlockFields ? (values.contractBlockValue != null ? Number(values.contractBlockValue) : null) : (contract.value ?? null);

  return {
    name: blockName,
    startDate: contractStartDate,
    endDate: contractEndDate,
    value: blockValue,
    usageLimitValue: contract.usageLimitValue ?? null,
    usageLimitUnit: contract.usageLimitUnit ?? null,
    paymentCycleValue: contract.paymentCycleValue ?? null,
    paymentCycleUnit: contract.paymentCycleUnit ?? null,
    durationValue: contract.durationValue ?? null,
    durationUnit: contract.durationUnit ?? null,
    renewalValue: contract.renewalValue ?? null,
    renewalUnit: contract.renewalUnit ?? null,
    allowsAccess: contract.allowsAccess !== false,
    homeStudioId: contract.homeStudioId ?? null,
    accessScope: Number(contract.accessScope || ACCESS_SCOPE_ANY_STUDIO),
    allowedStudioIds: (contract.allowedStudioIds || []).map(String),
    requiresNoArrears: contract.requiresNoArrears !== false,
    isActive: !contractEditIsLocked,
    paymentMethod: values.paymentMethod != null ? Number(values.paymentMethod) : contract.paymentMethod ?? null,
    sepaGroupId: sepaGroupIdToUse
  };
}

export function usePersonContractEditController({
  api,
  personId,
  canWriteContracts,
  sepaGroups,
  bookedContractIds,
  message,
  modal,
  text
}: UsePersonContractEditControllerArgs) {
  const financeOperation = useFinanceOperation(personId);
  const [contractEdit, setContractEdit] = React.useState<ContractRow | null>(null);
  const [contractEditOpen, setContractEditOpen] = React.useState(false);
  const contractEditSaving = financeOperation.pending;
  const [contractEditForm] = Form.useForm<PersonContractPackageEditFormValues>();
  const [contractEditHasDifferentPaymentMethods, setContractEditHasDifferentPaymentMethods] = React.useState(false);
  const [contractEditIsLocked, setContractEditIsLocked] = React.useState(false);
  const [contractEditPaymentMethod, setContractEditPaymentMethod] = React.useState<number | null>(null);
  const [contractEditSingleBlock, setContractEditSingleBlock] = React.useState<PersonServiceRow | null>(null);
  const [serviceEdit, setServiceEdit] = React.useState<PersonServiceRow | null>(null);
  const [serviceEditOpen, setServiceEditOpen] = React.useState(false);
  const [serviceEditIsLocked, setServiceEditIsLocked] = React.useState(false);
  const [serviceEditForm] = Form.useForm();

  React.useEffect(() => {
    if (!contractEditOpen) return;
    const timeoutId = setTimeout(() => {
      if (Number(contractEditPaymentMethod) !== PAYMENT_METHOD_SEPA) {
        const currentSepaGroup = contractEditForm.getFieldValue("sepaGroupId");
        if (currentSepaGroup != null) {
          contractEditForm.setFieldValue("sepaGroupId", null);
        }
      }
    }, SEPA_GROUP_RESET_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [contractEditPaymentMethod, contractEditOpen, contractEditForm]);

  const closeServiceEdit = React.useCallback(() => {
    setServiceEditOpen(false);
    setServiceEdit(null);
    setServiceEditIsLocked(false);
    serviceEditForm.resetFields();
  }, [serviceEditForm]);

  const markServiceEditSaved = React.useCallback(() => {
    setServiceEditOpen(false);
    setServiceEdit(null);
  }, []);

  const openServiceContractEdit = React.useCallback(
    (sr: PersonServiceRow) => {
      setServiceEdit(sr);
      setServiceEditIsLocked(sr.isActive === false);
      setServiceEditFormValues(serviceEditForm, sr, sepaGroups);
      setServiceEditOpen(true);
    },
    [sepaGroups, serviceEditForm]
  );

  const closeContractPackageEdit = React.useCallback(() => {
    setContractEditOpen(false);
    setContractEdit(null);
    setContractEditSingleBlock(null);
    setContractEditIsLocked(false);
    setContractEditPaymentMethod(null);
    setContractEditHasDifferentPaymentMethods(false);
    contractEditForm.resetFields();
  }, [contractEditForm]);

  const openContractPackageEdit = React.useCallback(
    async (r: ContractRow, singleBlock: PersonServiceRow | null) => {
      setContractEdit(r);
      setContractEditSingleBlock(singleBlock);
      const contractsList = await api.get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`);
      const relatedContracts = (contractsList || []).filter(
        (c) => c.sourceContractPackageId && String(c.sourceContractPackageId) === String(r.id)
      );
      const allInactive = relatedContracts.length > 0 && relatedContracts.every((c) => c.isActive === false);
      const isEndedOrCancelled = r.status === CONTRACT_STATUS_CANCELLED || r.status === CONTRACT_STATUS_ENDED;
      const allLocked = !isEndedOrCancelled && allInactive;
      const paymentMethods = relatedContracts.map((c) => c.paymentMethod).filter((m): m is number => m != null);
      const uniquePaymentMethods = [...new Set(paymentMethods)];
      const hasDifferentPaymentMethods = uniquePaymentMethods.length > 1;
      const paymentMethodValue = hasDifferentPaymentMethods ? null : r.paymentMethod != null ? Number(r.paymentMethod) : null;
      setContractEditPaymentMethod(paymentMethodValue);
      let sepaGroupIdValue: string | null = null;
      if (r.sepaGroupId) {
        sepaGroupIdValue = String(r.sepaGroupId);
      } else {
        const sepaGroupIds = relatedContracts.map((c) => c.sepaGroupId).filter((g): g is string => g != null);
        const uniqueSepaGroupIds = [...new Set(sepaGroupIds)];
        const hasDifferentSepaGroups = uniqueSepaGroupIds.length > 1;
        if (!hasDifferentSepaGroups && uniqueSepaGroupIds.length === 1) {
          sepaGroupIdValue = String(uniqueSepaGroupIds[0]);
        }
      }
      const primaryContract = relatedContracts.find((c) => c.isPrimary === true);
      const startDateFromPrimary = primaryContract?.startDate
        ? dayjs(String(primaryContract.startDate).slice(0, ISO_DATE_LENGTH))
        : r.startDate
          ? dayjs(String(r.startDate).slice(0, ISO_DATE_LENGTH))
          : null;
      const endDateFromPrimary = primaryContract?.endDate
        ? dayjs(String(primaryContract.endDate).slice(0, ISO_DATE_LENGTH))
        : r.endDate
          ? dayjs(String(r.endDate).slice(0, ISO_DATE_LENGTH))
          : null;
      setContractEditIsLocked(allLocked);
      setContractEditHasDifferentPaymentMethods(hasDifferentPaymentMethods);
      contractEditForm.setFieldsValue({
        planName: String(r.planName || ""),
        studioId: r.studioId ?? null,
        startDate: startDateFromPrimary,
        endDate: endDateFromPrimary,
        closingDate: r.closingDate ? dayjs(String(r.closingDate).slice(0, ISO_DATE_LENGTH)) : null,
        restPeriodTemplateId: r.restPeriodTemplateId ?? null,
        cancellationModelId: r.cancellationModelId ?? null,
        paymentMethod: paymentMethodValue,
        sepaGroupId: Number(paymentMethodValue) === PAYMENT_METHOD_SEPA ? sepaGroupIdValue : null,
        note: r.note ?? null,
        ...(singleBlock
          ? {
              contractBlockName: singleBlock.name,
              contractBlockValue: singleBlock.value ?? null
            }
          : {
              contractBlockName: undefined,
              contractBlockValue: undefined
            })
      });
      setContractEditOpen(true);
      if (Number(paymentMethodValue) !== PAYMENT_METHOD_SEPA && sepaGroupIdValue != null) {
        setTimeout(() => contractEditForm.setFieldValue("sepaGroupId", null), SEPA_GROUP_PREFILL_RESET_DELAY_MS);
      }
    },
    [api, contractEditForm, personId]
  );

  const tryOpenCombinedOrServiceEdit = React.useCallback(
    async (sr: PersonServiceRow) => {
      if (!personId || !sr.sourceContractPackageId) {
        openServiceContractEdit(sr);
        return;
      }
      const contractsList = await api.get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`);
      const related = (contractsList || []).filter(
        (c) => c.sourceContractPackageId && String(c.sourceContractPackageId) === String(sr.sourceContractPackageId)
      );
      if (related.length === 1) {
        const pkgs = await api.get<ApiContractPackageDto[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`);
        const pkg = (pkgs || []).find((p) => String(p.id) === String(sr.sourceContractPackageId));
        if (pkg) {
          await openContractPackageEdit(mapApiPackageToContractRow(pkg), sr);
          return;
        }
      }
      openServiceContractEdit(sr);
    },
    [api, openContractPackageEdit, openServiceContractEdit, personId]
  );

  const onContractsTableRowDoubleClick = React.useCallback(
    (record: ContractRow) => {
      if (!canWriteContracts) return;
      if (record.sourceContractPackageId != null) {
        void tryOpenCombinedOrServiceEdit(record as unknown as PersonServiceRow);
        return;
      }
      const children = record.children || [];
      const single = children.length === 1 ? children[0] : record.singleBlockChild ?? null;
      void openContractPackageEdit(record, single);
    },
    [canWriteContracts, openContractPackageEdit, tryOpenCombinedOrServiceEdit]
  );

  const saveContractPackageEdit = React.useCallback(async () => {
    if (!contractEdit) return;

    try {
      const values = await contractEditForm.validateFields();
      const startIso = values.startDate ? dayjs(values.startDate).format("YYYY-MM-DD") : null;
      if (!startIso) throw new Error(text("Start date is required"));
      const endIso = values.endDate ? dayjs(values.endDate).format("YYYY-MM-DD") : null;
      const planTrimmed = typeof values.planName === "string" ? values.planName.trim() : "";
      if (!planTrimmed) throw new Error(text("Contract package name required"));

      try {
        const paymentMethodValue = values.paymentMethod != null ? Number(values.paymentMethod) : null;
        const sepaGroupIdFromForm = contractEditForm.getFieldValue("sepaGroupId");
        const sepaGroupIdValue = values.sepaGroupId ?? sepaGroupIdFromForm;
        const sepaGroupIdToSend = normalizeSepaGroupId(sepaGroupIdValue, paymentMethodValue);
        const useMergedBlockFields = contractEditSingleBlock != null;
        const requestBody = buildContractPackageRequestBody(
          contractEdit,
          values,
          contractEditIsLocked,
          sepaGroupIdToSend,
          planTrimmed,
          startIso,
          endIso
        );

        await financeOperation.execute(async (committed) => {
          await api.put(`/api/v1/contract-packages/${encodeURIComponent(contractEdit.id)}`, requestBody);
          committed();

          const contractsList = await api
            .get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`);
          const relatedContracts = (contractsList || []).filter(
            (c) => c.sourceContractPackageId && String(c.sourceContractPackageId) === String(contractEdit.id)
          );

          if (relatedContracts.length > 0) {
            await Promise.all(
              relatedContracts.map((contract) =>
                api.put(
                  `/api/v1/contracts/${encodeURIComponent(String(contract.id))}`,
                  buildRelatedContractRequestBody(
                    contract,
                    values,
                    useMergedBlockFields,
                    contractEditIsLocked,
                    startIso,
                    endIso,
                    sepaGroupIdValue
                  )
                )
              )
            );
          }

          message.success(text("Saved."));
          closeContractPackageEdit();
        });
      } catch (e) {
        message.error(displayContractPackageSaveError(e, text));
      }
    } catch (e) {
      message.error(displayContractPackageSaveError(e, text));
    }
  }, [
    api,
    closeContractPackageEdit,
    contractEdit,
    contractEditForm,
    contractEditIsLocked,
    contractEditSingleBlock,
    message,
    personId,
    text,
    financeOperation
  ]);

  const deleteContractPackageEdit = React.useCallback(async () => {
    if (!contractEdit) return;
    const cid = String(contractEdit.id);
    if (bookedContractIds.has(cid)) {
      message.error(text("Cannot delete: payments have already been booked for this contract."));
      return;
    }

    const [contractsList, walletsList] = await Promise.all([
      api.get<ApiPersonServiceDto[]>(`/api/v1/contracts?personId=${encodeURIComponent(personId)}`),
      api.get<ApiWalletDto[]>(`/api/v1/wallets?personId=${encodeURIComponent(personId)}`)
    ]);
    const relatedServices = (contractsList || []).filter((s) => s.sourceContractPackageId && String(s.sourceContractPackageId) === cid);
    const relatedWallets = (walletsList || []).filter((w) => w.contractPackageId && String(w.contractPackageId) === cid);
    const contractName = String(contractEdit.planName || "").trim() || text("Unnamed contract package");

    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Delete contract package"),
        content: (
          <div>
            <Typography.Paragraph>
              {text("Delete contract package confirm").replace("{0}", contractName)}
            </Typography.Paragraph>
            {relatedServices.length > 0 && (
              <>
                <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.sm }}>
                  {text("The following sub-contracts will also be deleted:")}
                </Typography.Paragraph>
                <ul style={{ margin: 0, paddingLeft: designSpacing.contentGap, marginBottom: relatedWallets.length > 0 ? 12 : 0 }}>
                  {relatedServices.map((service, idx) => (
                    <li key={idx}>
                      <Typography.Text>
                        {String(service.name || "").trim() || text("Unnamed sub-contract")}
                      </Typography.Text>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {relatedWallets.length > 0 && (
              <>
                <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.sm }}>
                  {text("The following wallets will also be deleted:")}
                </Typography.Paragraph>
                <ul style={{ margin: 0, paddingLeft: designSpacing.contentGap }}>
                  {relatedWallets.map((wallet, idx) => (
                    <li key={idx}>
                      <Typography.Text>
                        {String(wallet.walletProductNameSnapshot || wallet.walletProductName || "").trim() || text("Unnamed wallet")}
                      </Typography.Text>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ),
        okText: text("Delete"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;

    try {
      await financeOperation.execute(async (committed) => {
        await api.del(`/api/v1/contract-packages/${encodeURIComponent(cid)}`);
        committed();
        message.success(text("Contract package deleted."));
        closeContractPackageEdit();
      });
    } catch {
      // Central API toast shows error.
    }
  }, [
    api,
    bookedContractIds,
    closeContractPackageEdit,
    contractEdit,
    message,
    modal,
    personId,
    text,
    financeOperation
  ]);

  return {
    contractEdit,
    contractEditOpen,
    contractEditSaving,
    contractEditForm,
    contractEditHasDifferentPaymentMethods,
    contractEditIsLocked,
    setContractEditIsLocked,
    setContractEditPaymentMethod,
    contractEditSingleBlock,
    serviceEdit,
    serviceEditOpen,
    serviceEditIsLocked,
    setServiceEditIsLocked,
    serviceEditForm,
    openContractPackageEdit,
    tryOpenCombinedOrServiceEdit,
    onContractsTableRowDoubleClick,
    closeContractPackageEdit,
    saveContractPackageEdit,
    deleteContractPackageEdit,
    closeServiceEdit,
    markServiceEditSaved
  };
}
